import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { formatDateTime } from '@app/utils/format';
import { ApiResponseError, buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { browserTimeZone } from '@app/utils/chatTimeContext';
import { useConfig } from '@config/provider';
import { streamSse } from '@app/utils/sse';
import { appendRunActivity, buildRunActivityEntry, summarizeRunEvent, type RunActivityEntry, type RunTraceLevel, RUN_TRACE_LEVEL_OPTIONS } from '@app/utils/chatRunTrace';
import type { MembershipSummary } from '@app/types/org-console';
import {
  ChatMessageContentBlocks,
  getPromptAnswerState,
  type ChatChoiceOptionRecord,
  type ChatChoicePromptBlock,
  type ChatInteractRequestContentBlock,
  type ChatMessageContentEvent,
} from '@app/components/chat/ChatMessageContentBlocks';
import { useChatPromptInteractions } from '@app/components/chat/useChatPromptInteractions';
import {
  formatArtifactSize,
  isFileArtifactKind,
  mapAttachmentsToArtifacts,
  type ChatAttachmentArtifact,
  type ChatMessageAttachmentRecord
} from '@app/components/chat/chatAttachments';
import ChatFilePreviewPane from '@app/components/chat/ChatFilePreviewPane';
import ComposerAttachmentPicker from '@app/components/chat/ComposerAttachmentPicker';
import {
  deriveFileDownloadName,
  downloadBlobAsFile,
  formatFilePreviewErrorMessage,
  hasMoreFileText,
  inferReadableUploadMime,
  parseArtifactDocument,
  parseUploadedFileRecord,
  resolveArtifactPathWithContent,
  type ChatArtifactLike,
  type FileTextSliceRecord,
  type UploadedFileRecord,
} from '@app/components/chat/chatArtifactUtils';
import {
  createAndDownloadOutputExport,
  OUTPUT_EXPORT_FORMAT_LABELS,
  resolveArtifactExportFormats,
  resolveDefaultArtifactExportFormat,
  type OutputExportFormat,
} from '@app/components/chat/outputExportTransport';
import {
  downloadManagedFileBytes,
  loadManagedFilePreview,
} from '@app/components/chat/filePreviewTransport';
import type { ArtifactDocumentschema, ChatFormResponseEntryschema } from '@reap/openapi-public-v1';
import type {
  IncidentChatMessageAttachment,
  IncidentChatMessageContentBlock,
  IncidentChatMessageRecord,
  IncidentChatMessagesResponse
} from '@app/pages/org/incidents/unifiedTypes';

interface IncidentConversationPanelProps {
  threadId?: string;
}

interface PendingComposerFile {
  localId: string;
  source: 'local' | 'promoted';
  file?: File;
  fileRecord?: UploadedFileRecord;
  status: 'pending' | 'uploading' | 'uploaded';
  uploadedAttachment?: Record<string, unknown>;
}

interface ChatTimelineEvent extends ChatMessageContentEvent {
  role: 'assistant' | 'user';
  timestamp: string;
  createdBy?: string;
  attachments?: ChatMessageAttachmentRecord[];
  attachmentArtifacts?: ChatAttachmentArtifact[];
}

interface ArtifactPreviewState {
  loading: boolean;
  content: string;
  error?: string;
  isFull?: boolean;
  objectUrl?: string;
  doc?: ArtifactDocumentschema | null;
  fileText?: FileTextSliceRecord;
  fileRecord?: UploadedFileRecord;
}

const POLL_INTERVAL_MS = 4000;

const createClientRequestId = () =>
  `incident-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const extractRunIdFromLocation = (location: string | null): string | null => {
  if (!location) {
    return null;
  }
  const match = location.match(/chat\/runs\/([^/?]+)/i);
  return match ? match[1] ?? null : null;
};

const isTerminalRunStatus = (value: unknown): boolean => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (
    normalized === 'succeeded' ||
    normalized === 'failed' ||
    normalized === 'cancelled' ||
    normalized === 'canceled' ||
    normalized === 'rejected' ||
    normalized === 'completed'
  );
};

const sortMessages = (messages: IncidentChatMessageRecord[]): IncidentChatMessageRecord[] =>
  [...messages].sort((left, right) => {
    const leftTime = left.createdAt ? Date.parse(left.createdAt) : 0;
    const rightTime = right.createdAt ? Date.parse(right.createdAt) : 0;
    return leftTime - rightTime;
  });

const buildMessageSignature = (messages: IncidentChatMessageRecord[]): string =>
  messages
    .map((message) => {
      const content = Array.isArray(message.content)
        ? message.content
            .map((block) => {
              const type = typeof block.type === 'string' ? block.type : 'unknown';
              const text = 'text' in block && typeof block.text === 'string' ? block.text : '';
              const prompt = 'prompt' in block && typeof block.prompt === 'string' ? block.prompt : '';
              return `${type}:${text}:${prompt}`;
            })
            .join('|')
        : '';
      const attachmentCount = Array.isArray(message.attachments) ? message.attachments.length : 0;
      return [message.id ?? '', message.createdAt ?? '', message.runId ?? '', message.role ?? '', attachmentCount, content].join('::');
    })
    .join('||');

const revokeArtifactObjectUrl = (objectUrls: Record<string, string>, artifactId: string) => {
  const objectUrl = objectUrls[artifactId];
  if (!objectUrl) {
    return;
  }
  URL.revokeObjectURL(objectUrl);
  delete objectUrls[artifactId];
};

const IncidentConversationPanel = ({ threadId = '' }: IncidentConversationPanelProps) => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource, fetchWithAuthRetry } = useAuthFetch();

  const [messages, setMessages] = useState<IncidentChatMessageRecord[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [memberNamesByUserId, setMemberNamesByUserId] = useState<Record<string, string>>({});
  const [composerText, setComposerText] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingComposerFile[]>([]);
  const [traceLevel, setTraceLevel] = useState<RunTraceLevel>('summary');
  const [streamingRunId, setStreamingRunId] = useState<string | null>(null);
  const [runStatusText, setRunStatusText] = useState<string | null>(null);
  const [runActivityTrail, setRunActivityTrail] = useState<RunActivityEntry[]>([]);
  const [sending, setSending] = useState(false);
  const [artifactPreview, setArtifactPreview] = useState<Record<string, ArtifactPreviewState>>({});
  const [artifactExportFormats, setArtifactExportFormats] = useState<Record<string, OutputExportFormat>>({});
  const [selectedArtifact, setSelectedArtifact] = useState<ChatAttachmentArtifact | null>(null);

  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);
  const lastMessageSignatureRef = useRef('');
  const lastRenderedMessageCountRef = useRef(0);
  const artifactObjectUrlsRef = useRef<Record<string, string>>({});

  const requestOrgApi = useCallback(
    async (resource: string, init: RequestInit = {}) => {
      if (!orgId) {
        throw new Error('Organization is not available.');
      }
      return fetchWithAuthRetry(buildOrgUrl(basePath, orgId, resource), init);
    },
    [basePath, fetchWithAuthRetry, orgId],
  );

  const loadMessages = useCallback(async (options?: { silent?: boolean }) => {
    if (!orgId || !threadId) {
      setMessages([]);
      setMessagesError(null);
      lastMessageSignatureRef.current = '';
      return;
    }
    const silent = options?.silent === true;
    if (!silent) {
      setMessagesLoading(true);
    }
    try {
      const payload = await fetchOrgResource<IncidentChatMessagesResponse>(
        basePath,
        orgId,
        `chat/threads/${encodeURIComponent(threadId)}/messages?limit=100&order=desc`
      );
      const nextMessages = sortMessages(payload.items ?? []);
      const nextSignature = buildMessageSignature(nextMessages);
      if (nextSignature !== lastMessageSignatureRef.current) {
        lastMessageSignatureRef.current = nextSignature;
        setMessages(nextMessages);
      }
      setMessagesError(null);
    } catch (fetchError) {
      if (!silent) {
        setMessages([]);
        lastMessageSignatureRef.current = '';
      }
      setMessagesError(fetchError instanceof Error ? fetchError.message : 'Failed to load incident conversation.');
    } finally {
      if (!silent) {
        setMessagesLoading(false);
      }
    }
  }, [basePath, fetchOrgResource, orgId, threadId]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!orgId) {
      setMemberNamesByUserId({});
      return;
    }
    let active = true;
    void fetchOrgResource<{ items?: MembershipSummary[] }>(basePath, orgId, 'memberships?limit=200&expand=user')
      .then((payload) => {
        if (!active) {
          return;
        }
        const lookup: Record<string, string> = {};
        for (const membership of payload.items ?? []) {
          const userId = membership.userId?.trim();
          if (!userId) {
            continue;
          }
          const displayName =
            membership.user?.displayName?.trim() ||
            membership.user?.name?.trim() ||
            membership.user?.email?.trim() ||
            userId;
          lookup[userId] = displayName;
        }
        setMemberNamesByUserId(lookup);
      })
      .catch(() => {
        if (active) {
          setMemberNamesByUserId({});
        }
      });
    return () => {
      active = false;
    };
  }, [basePath, fetchOrgResource, orgId]);

  useEffect(() => {
    if (!orgId || !threadId) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      if (streamingRunId || sending) {
        return;
      }
      void loadMessages({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [loadMessages, orgId, sending, streamingRunId, threadId]);

  const messageEvents = useMemo<ChatTimelineEvent[]>(
    () =>
      messages.map((message) => {
        const role = message.role === 'assistant' ? 'assistant' : 'user';
        return {
          id: message.id ?? `${role}-${message.createdAt ?? Date.now()}`,
          messageId: message.id,
          role,
          type: 'message',
          text: resolveMessageText(message.content ?? []),
          contentBlocks: message.content ?? undefined,
          timestamp: message.createdAt ?? new Date().toISOString(),
          createdBy: message.createdBy,
          attachments: (message.attachments as ChatMessageAttachmentRecord[] | undefined) ?? undefined,
          attachmentArtifacts: mapAttachmentsToArtifacts(message as { attachments?: ChatMessageAttachmentRecord[]; role?: string })
        };
      }),
    [messages]
  );

  const promptAnswerState = useMemo(() => getPromptAnswerState(messageEvents), [messageEvents]);
  const selectedArtifactPreview = selectedArtifact ? artifactPreview[selectedArtifact.id] : undefined;
  const selectedArtifactIsFile = isFileArtifactKind(selectedArtifact?.kind);
  const resolveMessageAuthorLabel = useCallback(
    (event: ChatTimelineEvent) => {
      if (event.role === 'assistant') {
        return 'REAP';
      }
      const createdBy = event.createdBy?.trim();
      if (!createdBy) {
        return 'Operator';
      }
      return memberNamesByUserId[createdBy] ?? 'Operator';
    },
    [memberNamesByUserId]
  );

  useEffect(() => {
    if (!timelineRef.current) {
      return;
    }
    if (messageEvents.length === lastRenderedMessageCountRef.current && !runStatusText) {
      return;
    }
    lastRenderedMessageCountRef.current = messageEvents.length;
    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [messageEvents, runStatusText]);

  useEffect(
    () => () => {
      Object.values(artifactObjectUrlsRef.current).forEach((objectUrl) => {
        URL.revokeObjectURL(objectUrl);
      });
      artifactObjectUrlsRef.current = {};
    },
    []
  );

  const interactWithThread = useCallback(
    async (
      nextThreadId: string,
      options: {
        content?: ChatInteractRequestContentBlock[];
        attachments?: Array<Record<string, unknown>>;
      }
    ) => {
      if (!orgId) {
        throw new Error('Organization is not available.');
      }
      const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `chat/threads/${encodeURIComponent(nextThreadId)}/interact`), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...(options.content && options.content.length > 0 ? { content: options.content } : {}),
          ...(options.attachments && options.attachments.length > 0 ? { attachments: options.attachments } : {}),
          ...(browserTimeZone() ? { context: { timeZone: browserTimeZone() } } : {}),
          trace: { level: traceLevel }
        })
      });
      if (!response.ok) {
        throw new Error(`Failed to send message (status ${response.status}).`);
      }
      const payload = (await response.json()) as IncidentChatMessageRecord;
      setMessages((prev) => sortMessages([...prev, payload]));
      const runId = extractRunIdFromLocation(response.headers.get('Location'));
      if (runId) {
        setStreamingRunId(runId);
        setRunStatusText('Working…');
        setRunActivityTrail([]);
      } else {
        setRunStatusText(null);
        setRunActivityTrail([]);
        void loadMessages();
      }
      return payload;
    },
    [basePath, fetchWithAuthRetry, loadMessages, orgId, traceLevel]
  );

  const resolveArtifactPath = useCallback(
    (artifact: ChatArtifactLike): string | null => {
      if (!artifact.id) {
        return null;
      }
      if (isFileArtifactKind(artifact.kind)) {
        return `files/${artifact.id}`;
      }
      return `chat/artifacts/${artifact.id}`;
    },
    []
  );

  const loadFileArtifactState = useCallback(
    async (artifactId: string, existingPreview: ArtifactPreviewState | undefined, full = false) => {
      if (!orgId) {
        throw new Error('Organization is not available.');
      }
      return loadManagedFilePreview(
        {
          basePath,
          orgId,
          fetchWithAuthRetry,
        },
        artifactId,
        {
          fileRecord: existingPreview?.fileRecord,
          fileText: full ? existingPreview?.fileText : undefined,
        },
        full,
      );
    },
    [basePath, fetchWithAuthRetry, orgId]
  );

  const loadArtifactPreview = useCallback(
    async (artifact: ChatAttachmentArtifact, full = false) => {
      if (!orgId || !artifact.id) {
        return;
      }
      setArtifactPreview((prev) => ({
        ...prev,
        [artifact.id]: {
          ...(prev[artifact.id] ?? { content: '' }),
          loading: true,
          error: undefined
        }
      }));
      try {
        if (isFileArtifactKind(artifact.kind)) {
          const existingPreview = artifactPreview[artifact.id];
          const loadedPreview = await loadFileArtifactState(artifact.id, existingPreview, full);
          setArtifactPreview((prev) => {
            revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
            if (loadedPreview.objectUrl) {
              artifactObjectUrlsRef.current[artifact.id] = loadedPreview.objectUrl;
            }
            return {
              ...prev,
              [artifact.id]: {
                loading: false,
                content: loadedPreview.content,
                fileText: loadedPreview.fileText,
                fileRecord: loadedPreview.fileRecord,
                objectUrl: loadedPreview.objectUrl,
                isFull: loadedPreview.isFull
              }
            };
          });
          return;
        }

        const path = resolveArtifactPathWithContent(resolveArtifactPath, artifact, full);
        if (!path) {
          return;
        }

        const mime = (artifact.mimeType ?? '').toLowerCase();
        if (mime.startsWith('image/') || mime === 'application/pdf') {
          const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, path), {
            headers: { Accept: mime || '*/*' }
          });
          if (!response.ok) {
            throw new Error(`Failed to load artifact (status ${response.status}).`);
          }
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setArtifactPreview((prev) => {
            const current = prev[artifact.id];
            if (current?.objectUrl && current.objectUrl !== objectUrl) {
              revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
            }
            artifactObjectUrlsRef.current[artifact.id] = objectUrl;
            return {
              ...prev,
              [artifact.id]: {
                loading: false,
                content: objectUrl,
                objectUrl,
                isFull: true
              }
            };
          });
          return;
        }

        const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, path), {
          headers: { Accept: 'application/vnd.reap.artifact+json' }
        });
        if (!response.ok) {
          throw new Error(`Failed to load artifact (status ${response.status}).`);
        }
        const raw = await response.text();
        const doc = parseArtifactDocument(raw);
        const content = doc ? resolveArtifactDocumentText(doc, full) : raw;
        setArtifactPreview((prev) => {
          revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
          return {
            ...prev,
            [artifact.id]: {
              loading: false,
              content,
              doc,
              isFull: full
            }
          };
        });
      } catch (error) {
        setArtifactPreview((prev) => {
          revokeArtifactObjectUrl(artifactObjectUrlsRef.current, artifact.id);
          return {
            ...prev,
            [artifact.id]: {
              ...(prev[artifact.id] ?? { content: '' }),
              loading: false,
              error: isFileArtifactKind(artifact.kind)
                ? formatFilePreviewErrorMessage(error)
                : error instanceof Error
                  ? error.message
                  : 'Failed to load artifact'
            }
          };
        });
      }
    },
    [artifactPreview, basePath, fetchWithAuthRetry, loadFileArtifactState, orgId, resolveArtifactPath]
  );

  const handleSelectArtifact = useCallback(
    async (artifact: ChatAttachmentArtifact) => {
      setSelectedArtifact(artifact);
      const existing = artifactPreview[artifact.id];
      if (!existing || existing.error) {
        await loadArtifactPreview(artifact, false);
      }
    },
    [artifactPreview, loadArtifactPreview]
  );

  const handleDownloadArtifact = useCallback(
    async (artifact: ChatAttachmentArtifact, explicitFormat?: OutputExportFormat) => {
      if (!orgId || !artifact.id) {
        return;
      }
      try {
        if (isFileArtifactKind(artifact.kind)) {
          const blob = await downloadManagedFileBytes(
            {
              basePath,
              orgId,
              fetchWithAuthRetry,
            },
            artifact.id,
            artifact.mimeType ?? 'application/octet-stream',
          );
          downloadBlobAsFile(
            blob,
            deriveFileDownloadName(artifact.id, artifactPreview[artifact.id]?.fileRecord, artifact.summary)
          );
          return;
        }
        const format = explicitFormat ?? artifactExportFormats[artifact.id] ?? resolveDefaultArtifactExportFormat(artifact);
        const fallbackStem =
          (typeof artifact.summary === 'string' && artifact.summary.trim().length > 0
            ? artifact.summary
            : `${artifact.kind ?? 'artifact'}-${artifact.id}`);
        const downloaded = await createAndDownloadOutputExport(
          requestOrgApi,
          {
            resourceRefs: [`artifact:${artifact.id}`],
            purpose: 'ui_download',
            bundleMode: 'single',
            ...(format ? { format } : {}),
          },
          fallbackStem,
        );
        downloadBlobAsFile(downloaded.blob, downloaded.fileName);
      } catch (error) {
        setArtifactPreview((prev) => ({
          ...prev,
          [artifact.id]: {
            ...(prev[artifact.id] ?? { content: '' }),
            loading: false,
            error: isFileArtifactKind(artifact.kind)
              ? formatFilePreviewErrorMessage(error)
              : error instanceof Error && error.message
                ? error.message
                : 'Failed to download artifact',
          }
        }));
      }
    },
    [artifactExportFormats, artifactPreview, basePath, fetchWithAuthRetry, orgId, requestOrgApi]
  );

  const renderArtifactDownloadAction = useCallback(
    (artifact: ChatAttachmentArtifact, buttonClassName: string, selectClassName?: string) => {
      if (isFileArtifactKind(artifact.kind)) {
        return null;
      }
      const formats = resolveArtifactExportFormats(artifact);
      const selectedFormat = artifactExportFormats[artifact.id] ?? resolveDefaultArtifactExportFormat(artifact);
      return (
        <>
          {formats.length > 1 ? (
            <label className="chat-export-format">
              <span className="chat-export-format__label">Format</span>
              <select
                className={selectClassName ?? 'chat-export-format__select'}
                value={selectedFormat ?? formats[0]}
                onChange={(event) =>
                  setArtifactExportFormats((previous) => ({
                    ...previous,
                    [artifact.id]: event.target.value as OutputExportFormat,
                  }))
                }
              >
                {formats.map((format) => (
                  <option key={format} value={format}>
                    {OUTPUT_EXPORT_FORMAT_LABELS[format]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className={buttonClassName}
            onClick={() => void handleDownloadArtifact(artifact, selectedFormat)}
          >
            Download
          </button>
        </>
      );
    },
    [artifactExportFormats, handleDownloadArtifact]
  );

  const handleOpenArtifactInNewTab = useCallback(
    async (artifact: ChatAttachmentArtifact) => {
      if (!orgId || isFileArtifactKind(artifact.kind)) {
        return;
      }
      const path = resolveArtifactPathWithContent(resolveArtifactPath, artifact, true);
      if (!path) {
        return;
      }
      const url = buildOrgUrl(basePath, orgId, path);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [basePath, orgId, resolveArtifactPath]
  );

  const submitPromptBlocks = useCallback(
    async (
      messageId: string | undefined,
      questionId: string | undefined,
      blockType: string | undefined,
      content: ChatInteractRequestContentBlock[]
    ) => {
      if (!threadId) {
        return;
      }
      await interactWithThread(threadId, { content });
    },
    [interactWithThread, threadId]
  );
  const {
    promptChoiceDrafts,
    promptInputDrafts,
    promptFormDrafts,
    promptSubmissionState,
    setPromptChoiceDrafts,
    setPromptInputDrafts,
    setPromptFormDrafts,
    submitSingleChoicePrompt,
    submitChoicePromptDraft,
    submitConfirmationPrompt,
    submitInputPrompt,
    submitFormPrompt
  } = useChatPromptInteractions({ submitBlocks: submitPromptBlocks });

  useEffect(() => {
    const runId = streamingRunId;
    if (!runId || !threadId || !orgId) {
      streamControllerRef.current?.abort();
      streamControllerRef.current = null;
      return;
    }

    const controller = new AbortController();
    streamControllerRef.current = controller;
    let cancelled = false;
    const streamUrl = new URL(buildOrgUrl(basePath, orgId, `chat/runs/${runId}/events`));

    void streamSse(
      streamUrl.toString(),
      {
        onEvent: (eventType, data, eventId) => {
          if (cancelled) {
            return;
          }
          let payload: Record<string, unknown> = {};
          try {
            payload = JSON.parse(data) as Record<string, unknown>;
          } catch {
            payload = {};
          }
          const summary = summarizeRunEvent(eventType, payload);
          if (summary) {
            setRunStatusText(summary);
          }
          const activity = buildRunActivityEntry(eventType, payload, eventId);
          if (activity) {
            setRunActivityTrail((prev) => appendRunActivity(prev, activity));
          }
          if (eventType === 'message') {
            void loadMessages();
          }
          if (eventType === 'error' || (eventType === 'status' && isTerminalRunStatus(payload.status))) {
            setStreamingRunId(null);
            void loadMessages();
          }
        },
        reconnect: {
          enabled: true,
          maxAttempts: 2,
          retryDelayMs: 1000
        }
      },
      controller.signal
    )
      .catch((error) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error('Failed to stream incident chat run', error);
        setStreamingRunId(null);
        setRunStatusText('Run stream disconnected');
        void loadMessages();
      })
      .finally(() => {
        if (streamControllerRef.current === controller) {
          streamControllerRef.current = null;
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [basePath, loadMessages, orgId, streamingRunId, threadId]);

  const handleSelectFiles = useCallback((files: File[]) => {
    if (files.length === 0) {
      return;
    }
    setPendingFiles((prev) => [
      ...prev,
      ...files.map((file) => ({
        localId: createClientRequestId(),
        source: 'local' as const,
        file,
        status: 'pending' as const
      }))
    ]);
    setComposerError(null);
  }, []);

  const handleAttachPromotedFile = useCallback((fileRecord: UploadedFileRecord) => {
    const resourceId = (fileRecord.id ?? '').trim();
    if (!resourceId) {
      return;
    }
    setComposerError(null);
    setPendingFiles((previous) => {
      if (previous.some((item) => item.source === 'promoted' && item.uploadedAttachment?.resourceId === resourceId)) {
        return previous;
      }
      return [
        ...previous,
        {
          localId: createClientRequestId(),
          source: 'promoted',
          fileRecord,
          status: 'uploaded',
          uploadedAttachment: {
            resourceType: 'file',
            resourceId,
            name: (fileRecord.filename ?? fileRecord.name ?? resourceId).trim() || resourceId,
            mimeType: (fileRecord.mimeType ?? '').trim() || undefined,
            sizeBytes: typeof fileRecord.sizeBytes === 'number' ? fileRecord.sizeBytes : undefined,
          },
        },
      ];
    });
  }, []);

  const removePendingFile = useCallback((localId: string) => {
    setPendingFiles((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  const uploadComposerFiles = useCallback(
    async (nextThreadId: string, files: PendingComposerFile[]) => {
      if (!orgId) {
        throw new Error('Organization is not available.');
      }
      const uploaded: Array<Record<string, unknown>> = [];
      for (const item of files) {
        if (item.source === 'promoted') {
          if (item.uploadedAttachment) {
            uploaded.push(item.uploadedAttachment);
          }
          continue;
        }
        if (!item.file) {
          continue;
        }
        setPendingFiles((prev) => prev.map((entry) => (entry.localId === item.localId ? { ...entry, status: 'uploading' } : entry)));
        const formData = new FormData();
        formData.append('file', item.file, item.file.name);
        formData.append('threadId', nextThreadId);
        formData.append('name', item.file.name);
        const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, 'files'), {
          method: 'POST',
          body: formData
        });
        if (!response.ok) {
          throw new Error(`Failed to upload ${item.file.name} (status ${response.status}).`);
        }
        const payload = parseUploadedFileRecord(await response.json());
        if (!payload) {
          throw new Error(`Upload response for ${item.file.name} did not match Fileschema.`);
        }
        const resourceId = typeof payload.id === 'string' ? payload.id.trim() : '';
        if (!resourceId) {
          throw new Error(`Upload response for ${item.file.name} did not include a file id.`);
        }
        uploaded.push({
          resourceType: 'file',
          resourceId,
          name: (payload.filename ?? item.file.name).trim() || item.file.name,
          mimeType: (payload.mimeType ?? inferReadableUploadMime(item.file)).trim() || undefined,
          sizeBytes: typeof payload.sizeBytes === 'number' ? payload.sizeBytes : item.file.size
        });
      }
      return uploaded;
    },
    [basePath, fetchWithAuthRetry, orgId]
  );

  const handleSendMessage = useCallback(async () => {
    const trimmed = composerText.trim();
    if (!threadId || sending || (!trimmed && pendingFiles.length === 0)) {
      return;
    }
    setSending(true);
    setComposerError(null);
    setRunStatusText('Preparing request…');
    try {
      const attachments = pendingFiles.length > 0 ? await uploadComposerFiles(threadId, [...pendingFiles]) : undefined;
      await interactWithThread(threadId, {
        ...(trimmed ? { content: [{ type: 'text', text: trimmed } satisfies ChatInteractRequestContentBlock] } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {})
      });
      setComposerText('');
      clearPendingFiles();
    } catch (error) {
      setPendingFiles((prev) => prev.map((item) => (item.status === 'uploading' ? { ...item, status: 'pending' } : item)));
      setComposerError(error instanceof Error ? error.message : 'Failed to send message');
      setRunStatusText(null);
      setStreamingRunId(null);
    } finally {
      setSending(false);
    }
  }, [clearPendingFiles, composerText, interactWithThread, pendingFiles, sending, threadId, uploadComposerFiles]);

  const handleComposerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const target = event.currentTarget;
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        const nextValue = `${target.value.slice(0, start)}\n${target.value.slice(end)}`;
        setComposerText(nextValue);
        requestAnimationFrame(() => {
          if (composerRef.current) {
            composerRef.current.selectionStart = composerRef.current.selectionEnd = start + 1;
          }
        });
        return;
      }
      event.preventDefault();
      void handleSendMessage();
    },
    [handleSendMessage]
  );

  const canSend = useMemo(() => !sending && threadId && (composerText.trim().length > 0 || pendingFiles.length > 0), [composerText, pendingFiles.length, sending, threadId]);

  return (
    <section className="section-card topology-workbench__chat-card unified-incident-chat">
      <div className="topology-workbench__chat-header">
        <div>
          <h2 className="topology-workbench__chat-title">Incident Chat</h2>
          <p className="topology-workbench__chat-subtitle">Shared incident thread across REAP and ticketing surfaces.</p>
        </div>
        {streamingRunId && <span className="topology-workbench__chat-badge">Running</span>}
      </div>

      <div className="topology-workbench__chat-timeline unified-incident-chat__timeline" ref={timelineRef}>
        {messagesLoading ? (
          <div className="topology-workbench__chat-empty">Loading messages…</div>
        ) : messagesError ? (
          <div className="topology-workbench__chat-empty topology-workbench__chat-empty--error">{messagesError}</div>
        ) : messageEvents.length === 0 ? (
          <div className="topology-workbench__chat-empty">
            {threadId ? 'Ask REAP about this incident, or attach supporting files.' : 'This incident has no chat thread yet.'}
          </div>
        ) : (
          messageEvents.map((event) => {
            const role = event.role === 'assistant' ? 'assistant' : 'user';
            return (
              <article
                key={event.id}
                className={`topology-workbench__chat-message topology-workbench__chat-message--${role} unified-incident-chat__message unified-incident-chat__message--${role}`}>
                <div className="unified-incident-chat__message-shell">
                  <div className="topology-workbench__chat-message-role unified-incident-chat__message-meta">
                    <span className="unified-incident-chat__message-author">{resolveMessageAuthorLabel(event)}</span>
                    <span className="unified-incident-chat__timestamp">{formatDateTime(event.timestamp)}</span>
                  </div>
                  <div className="topology-workbench__chat-message-text unified-incident-chat__message-bubble">
                    <ChatMessageContentBlocks
                      event={event}
                      answeredPromptKeys={promptAnswerState.answeredPromptKeys}
                      supersededPromptKeys={promptAnswerState.supersededPromptKeys}
                      latestResponsesByPromptKey={promptAnswerState.latestResponsesByPromptKey}
                      promptChoiceDrafts={promptChoiceDrafts}
                      promptInputDrafts={promptInputDrafts}
                      promptFormDrafts={promptFormDrafts}
                      promptSubmissionState={promptSubmissionState}
                      setPromptChoiceDrafts={setPromptChoiceDrafts}
                      setPromptInputDrafts={setPromptInputDrafts}
                      setPromptFormDrafts={setPromptFormDrafts}
                      submitSingleChoicePrompt={submitSingleChoicePrompt}
                      submitChoicePromptDraft={submitChoicePromptDraft}
                      submitConfirmationPrompt={submitConfirmationPrompt}
                      submitInputPrompt={submitInputPrompt}
                      submitFormPrompt={submitFormPrompt}
                    />
                  </div>
                  {(event.attachmentArtifacts ?? []).length > 0 && (
                    <div className="unified-incident-chat__artifact-list">
                      {(event.attachmentArtifacts ?? []).map((artifact) => {
                        const sizeLabel = formatArtifactSize(artifact.size);
                        const subtitle = [artifact.mimeType, sizeLabel].filter(Boolean).join(' · ');
                        return (
                          <button
                            key={`${artifact.kind}-${artifact.id}`}
                            type="button"
                            className={`unified-incident-chat__artifact-pill${selectedArtifact?.id === artifact.id ? ' unified-incident-chat__artifact-pill--active' : ''}`}
                            onClick={() => void handleSelectArtifact(artifact)}
                            title={subtitle || artifact.summary || artifact.id}
                          >
                            <span>{artifact.summary ?? artifact.id}</span>
                            {subtitle ? <span className="unified-incident-chat__artifact-pill-meta">{subtitle}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </article>
            );
          })
        )}
        {runStatusText ? <div className="topology-workbench__chat-status">{runStatusText}</div> : null}
        {runActivityTrail.length > 0 ? (
          <div className="topology-workbench__chat-activity" aria-label="Investigation activity">
            {runActivityTrail
              .slice()
              .reverse()
              .map((entry) => (
                <div key={entry.id} className={`topology-workbench__chat-activity-item topology-workbench__chat-activity-item--${entry.eventType}`}>
                  {entry.summary}
                </div>
              ))}
          </div>
        ) : null}
      </div>

      {selectedArtifact && (
        <div className="unified-incident-chat__artifact-preview">
          <div className="unified-incident-chat__artifact-preview-header">
            <div>
              <div className="unified-incident-chat__artifact-preview-title">{selectedArtifact.summary ?? selectedArtifact.id}</div>
              <div className="unified-incident-chat__artifact-preview-meta">
                {[selectedArtifact.mimeType, formatArtifactSize(selectedArtifact.size)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="unified-incident-chat__artifact-preview-actions">
              {!selectedArtifactIsFile && !selectedArtifactPreview?.isFull ? (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => void loadArtifactPreview(selectedArtifact, true)}
                  disabled={selectedArtifactPreview?.loading}
                >
                  Load full
                </button>
              ) : null}
              {!selectedArtifactIsFile ? (
                <button type="button" className="button button--ghost button--small" onClick={() => void handleOpenArtifactInNewTab(selectedArtifact)}>
                  Open raw
                </button>
              ) : null}
              {renderArtifactDownloadAction(
                selectedArtifact,
                'button button--ghost button--small',
                'chat-export-format__select chat-export-format__select--compact'
              )}
              <button type="button" className="button button--ghost button--small" onClick={() => setSelectedArtifact(null)}>
                Close
              </button>
            </div>
          </div>
          {selectedArtifactPreview?.loading ? <div className="topology-workbench__chat-status">Loading artifact…</div> : null}
          {selectedArtifactPreview?.error ? <div className="topology-workbench__chat-empty topology-workbench__chat-empty--error">{selectedArtifactPreview.error}</div> : null}
          {!selectedArtifactPreview?.loading && !selectedArtifactPreview?.error && selectedArtifactPreview
            ? renderArtifactPreview(
                selectedArtifact,
                selectedArtifactPreview,
                () => void loadArtifactPreview(selectedArtifact, true),
                (artifact) => void handleDownloadArtifact(artifact)
              )
            : null}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="topology-workbench__chat-files">
          {pendingFiles.map((item) => (
            <div key={item.localId} className="topology-workbench__chat-file">
              <div className="topology-workbench__chat-file-meta">
                <span>
                  {item.source === 'promoted'
                    ? (item.fileRecord?.filename ??
                      item.fileRecord?.name ??
                      (typeof item.uploadedAttachment?.resourceId === 'string' ? item.uploadedAttachment.resourceId : 'Promoted file'))
                    : item.file?.name ?? 'Local file'}
                </span>
                <span>
                  {Math.max(
                    1,
                    Math.round(
                      ((item.source === 'promoted' ? item.fileRecord?.sizeBytes : item.file?.size) ?? 0) / 1024
                    )
                  )}{' '}
                  KB
                </span>
                {item.status === 'uploading' && <span>Uploading…</span>}
                {item.source === 'promoted' && item.status !== 'uploading' && <span>Promoted file</span>}
              </div>
              {item.status !== 'uploading' && (
                <button type="button" className="button button--ghost button--small" onClick={() => removePendingFile(item.localId)}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="topology-workbench__chat-composer unified-incident-chat__composer">
        <textarea
          ref={composerRef}
          className="textarea topology-workbench__chat-input"
          rows={5}
          value={composerText}
          onChange={(event) => setComposerText(event.target.value)}
          onKeyDown={handleComposerKeyDown}
          placeholder={threadId ? 'Ask about the current incident…' : 'Incident chat is unavailable until a thread is created.'}
          disabled={!threadId || sending}
        />
        {composerError && <div className="topology-workbench__chat-error">{composerError}</div>}
        <div className="topology-workbench__chat-actions">
          <label className="topology-workbench__trace-level">
            <span>Detail</span>
            <select value={traceLevel} onChange={(event) => setTraceLevel(event.target.value as RunTraceLevel)} disabled={!threadId || sending}>
              {RUN_TRACE_LEVEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <ComposerAttachmentPicker
            disabled={!threadId || sending}
            requestOrgApi={requestOrgApi}
            onSelectLocalFiles={handleSelectFiles}
            onSelectPromotedFile={handleAttachPromotedFile}
            triggerClassName="button button--secondary"
            triggerAriaLabel="Attach file or promoted file"
            triggerContent="Attach files"
          />
          <button type="button" className="button" onClick={() => void handleSendMessage()} disabled={!canSend}>
            Send
          </button>
        </div>
      </div>
    </section>
  );
};

const resolveMessageText = (blocks: IncidentChatMessageContentBlock[]): string => {
  for (const block of blocks) {
    if (block.type === 'text' && typeof block.text === 'string' && block.text.trim()) {
      return block.text.trim();
    }
  }
  return '';
};

const formatFormResponseField = (field: ChatFormResponseEntryschema): string => {
  if (typeof field.valueText === 'string' && field.valueText.trim().length > 0) {
    return field.valueText.trim();
  }
  if (typeof field.valueNumber === 'number') {
    return String(field.valueNumber);
  }
  if (typeof field.valueBoolean === 'boolean') {
    return field.valueBoolean ? 'Yes' : 'No';
  }
  if (typeof field.valueChoiceId === 'string' && field.valueChoiceId.trim().length > 0) {
    return field.valueChoiceId.trim();
  }
  if (Array.isArray(field.valueChoiceIds) && field.valueChoiceIds.length > 0) {
    return field.valueChoiceIds.join(', ');
  }
  return '';
};

const resolveArtifactDocumentText = (doc: ArtifactDocumentschema, full: boolean): string => {
  const anyDoc = doc as any;
  const view = Array.isArray(anyDoc.views) && anyDoc.views.length > 0 ? anyDoc.views[0] : undefined;
  const content = view?.content ?? anyDoc.content;

  if (content?.type === 'commandOutput') {
    const fullText = typeof content.fullText === 'string' ? content.fullText : '';
    const previewText = typeof content.previewText === 'string' ? content.previewText : '';
    return full && fullText ? fullText : previewText || fullText || '';
  }

  if (content?.type === 'table') {
    const rows = Array.isArray(content.rows) ? content.rows : [];
    return JSON.stringify(rows, null, 2);
  }

  return JSON.stringify(content ?? anyDoc, null, 2);
};

const renderArtifactPreview = (
  artifact: ChatAttachmentArtifact,
  preview: ArtifactPreviewState,
  onLoadMore: () => void,
  onDownload: (artifact: ChatAttachmentArtifact) => void
) => {
  if (isFileArtifactKind(artifact.kind)) {
    return (
      <ChatFilePreviewPane
        artifactId={artifact.id}
        file={preview.fileRecord}
        mimeType={artifact.mimeType}
        sizeBytes={artifact.size}
        text={preview.content}
        fileText={preview.fileText}
        objectUrl={preview.objectUrl}
        loading={preview.loading}
        onDownload={() => onDownload(artifact)}
        onLoadMore={onLoadMore}
        renderPreview={(artifactId, content, options) => (
          <>
            {(preview.fileText?.lines ?? content.split('\n').map((text, index) => ({ lineNo: index + 1, text }))).map((line) => (
              <span
                key={`${artifactId}-line-${line.lineNo}`}
                className={options?.showLineNumbers ? 'chat-artifact-line chat-artifact-line--numbered' : 'chat-artifact-line'}
                data-line={line.lineNo}
              >
                {line.text || ' '}
              </span>
            ))}
          </>
        )}
      />
    );
  }
  const mime = (artifact.mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/') && preview.objectUrl) {
    return <img className="unified-incident-chat__artifact-preview-image" src={preview.objectUrl} alt={artifact.summary ?? 'Artifact preview'} />;
  }
  if (mime === 'application/pdf' && preview.objectUrl) {
    return <iframe className="unified-incident-chat__artifact-preview-frame" src={preview.objectUrl} title={artifact.summary ?? 'Artifact preview'} />;
  }
  return <pre className="unified-incident-chat__artifact-preview-body">{preview.content || 'No preview available.'}</pre>;
};

const _keepTsHappy = formatFormResponseField;
void _keepTsHappy;

const _attachmentTypeAnchor: IncidentChatMessageAttachment | null = null;
void _attachmentTypeAnchor;

export default IncidentConversationPanel;
